'use client';

import { useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  SignalListGroupByFieldSchema,
  type SignalListGroupByField,
} from '@terra/shared-contracts-signals';
import { ListPageGroupByOptionsMenu } from '../../common/ListPageGroupByOptionsMenu';

type SignalsViewOptionsMenuProps = {
  canGroup: boolean;
  showSourceOption: boolean;
  showAssetOption: boolean;
  groupByField: SignalListGroupByField | null;
  onGroupByFieldChange: (field: SignalListGroupByField | null) => void;
  areAllGroupsCollapsed: boolean;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  children?: ReactNode;
};

export function SignalsViewOptionsMenu({
  canGroup,
  showSourceOption,
  showAssetOption,
  groupByField,
  onGroupByFieldChange,
  areAllGroupsCollapsed,
  onExpandAllGroups,
  onCollapseAllGroups,
  children,
}: SignalsViewOptionsMenuProps) {
  const tTable = useTranslations('proxy.signals.table');
  const tFilters = useTranslations('proxy.signals.filters');
  const tLifecycle = useTranslations('designSystem.signals.lifecycle');
  const tItemKinds = useTranslations('home.recentItems.kinds');
  const options = useMemo(() => {
    if (!canGroup) {
      return [];
    }

    const fieldLabels: Record<SignalListGroupByField, string> = {
      status: tLifecycle('status.label'),
      stage: tLifecycle('stage.label'),
      category: tFilters('category'),
      source: tTable('source'),
      endpoint: tTable('relatedEndpoint'),
      execution: tTable('relatedExecution'),
      finding: tTable('relatedFinding'),
      asset: tItemKinds('ai-component'),
    };

    return SignalListGroupByFieldSchema.options
      .filter((field) => showSourceOption || field !== 'source')
      .filter((field) => showAssetOption || field !== 'asset')
      .map((field) => ({ value: field, label: fieldLabels[field] }));
  }, [
    canGroup,
    showAssetOption,
    showSourceOption,
    tFilters,
    tItemKinds,
    tLifecycle,
    tTable,
  ]);

  return (
    <ListPageGroupByOptionsMenu
      options={options}
      groupByField={groupByField}
      onGroupByFieldChange={onGroupByFieldChange}
      areAllGroupsCollapsed={areAllGroupsCollapsed}
      onExpandAllGroups={onExpandAllGroups}
      onCollapseAllGroups={onCollapseAllGroups}
    >
      {children}
    </ListPageGroupByOptionsMenu>
  );
}
